import { useMemo } from 'react';
import { FileText, ExternalLink } from 'lucide-react';
import type { SearchResult } from '@/services/external-chat/types';

interface SearchResultCardProps {
  result: SearchResult;
  query: string;
  fontColor: string;
  linkColor: string;
  borderRadius?: string;
}

function extractDomain(url: string): string {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return '';
  }
}

export const SearchResultCard = ({
  result,
  query: _query,
  fontColor,
  linkColor,
  borderRadius = '8px',
}: SearchResultCardProps) => {
  const domain = useMemo(() => {
    if (result.source_type === 'web' && result.url) {
      return extractDomain(result.url);
    }
    return result.metadata.original_filename || 'PDF';
  }, [result.source_type, result.url, result.metadata.original_filename]);

  return (
    <a
      href={result.url || '#'}
      target='_blank'
      rel='noopener noreferrer'
      className='result-card block border p-3 transition-colors hover:bg-black/[0.02]'
      style={{ borderColor: fontColor + '15', borderRadius }}
    >
      <div className='flex items-start gap-2'>
        {result.source_type === 'pdf' ? (
          <FileText className='mt-0.5 size-4 shrink-0 opacity-40' />
        ) : (
          <ExternalLink className='mt-0.5 size-4 shrink-0 opacity-40' />
        )}
        <div className='min-w-0 flex-1'>
          <h3
            className='line-clamp-1 text-sm font-medium leading-snug'
            style={{ color: linkColor }}
          >
            {result.title}
          </h3>
          <p
            className='mt-0.5 line-clamp-2 text-xs leading-relaxed opacity-70'
            style={{ color: fontColor }}
          >
            {result.snippet}
          </p>
          <div className='mt-1.5 flex items-center gap-2 text-xs opacity-50'>
            <span>{domain}</span>
            {result.publish_date && (
              <>
                <span>&middot;</span>
                <span>
                  {new Date(result.publish_date).toLocaleDateString()}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </a>
  );
};
